import clsx from 'clsx'
import { campaignApi } from 'features/campaigns'
import * as Scheduled from 'features/scheduled-messages'
import { appDay } from 'shared/day'
import { typo } from 'shared/styles'
import { StackIcon } from 'shared/ui'

import type { Execution } from '../../types'

import { useMostRelevant } from '../../hooks'
import { InProgressList } from './InProgress'
import { InQueueList } from './InQueueList'
import { MessageTextPreview } from './MessagePreview'
import { SkeletonList, TimeSpanFull } from './Scheduled'
import css from './UpcomingEvents.module.css'

interface TextBlockProps {
	description: string
	title: string
}

const Text: Record<string, TextBlockProps> = {
	NoActivities: {
		description: 'Add this contact to a campaign or schedule a message',
		title: 'No upcoming activities so far.'
	},
	Paused: {
		description: 'No planned activities',
		title: 'Campaign is paused.'
	}
}

const TextBlock = ({ description, title }: TextBlockProps) => [
	<h4 className={css.title} key='t'>
		{title}
	</h4>,
	<p className={css.description} key='d'>
		{description}
	</p>
]

function ExecutionChecker() {
	const { currentData: execution, isSuccess } = useMostRelevant()

	const isNoExecution = isSuccess && typeof execution === 'string'
	const isExistExecution = isSuccess && typeof execution === 'object'

	if (isNoExecution) {
		return <CheckScheduled />
	}

	if (isExistExecution) {
		return <StateChecker {...execution} />
	}

	return <SkeletonList />
}

export const UpcomingEvents = () => (
	<section className={clsx(css.wrapper, typo.buttonSm)}>
		<h3 className={css.label}>Upcoming activities</h3>
		<ExecutionChecker />
	</section>
)

function CheckPaused({ campaignId }: { campaignId: string }) {
	const campaignQuery = campaignApi.useGetCampaignsByIdQuery(campaignId, {
		skip: !campaignId
	})

	const campaign =
		campaignId &&
		campaignQuery.isSuccess &&
		campaignQuery.currentData?.id === campaignId
			? campaignQuery.currentData
			: null

	if (!campaign) {
		return <SkeletonList />
	}

	return campaign.state === 'Stopped' ? (
		<TextBlock {...Text.Paused} />
	) : (
		<CheckProcessCampaign />
	)
}

function CheckProcessCampaign() {
	const { currentData: execution, isSuccess } = useMostRelevant()

	const isExistExecution = isSuccess && typeof execution === 'object'

	if (!isExistExecution) {
		return <SkeletonList />
	}

	const notSettledIndex = execution.step_executions.findIndex(
		s => !s.is_settled
	)
	if (notSettledIndex < 0) {
		console.error('It is a bug')
		//you could report a bug
		return
	}

	const notSettledStep = execution.step_executions[notSettledIndex]
	switch (notSettledStep.state) {
		case 'InProgress':
			return (
				<InProgressList
					execution={execution}
					inProgressStepNumber={notSettledIndex}
				/>
			)

		case 'InQueue':
			return (
				<InQueueList
					{...notSettledStep}
					currentIndex={notSettledIndex}
					executionId={execution.id}
				/>
			)
	}
}

function CheckScheduled() {
	const { currentData, isFetching } = Scheduled.hooks.useChatScheduled()

	if (isFetching) {
		return <SkeletonList />
	}

	if (currentData?.total) {
		const lasts = currentData.documents.slice(0, 2)

		return (
			<ol className={css.list}>
				{lasts.map(message => (
					<li className={css.item} key={message.id}>
						<StackIcon className={css.scheduled} icon='scheduled' />
						<span className={css.text}>
							Send scheduled <MessageTextPreview text={message.text ?? ''} />
						</span>

						{message.time && <TimeSpanFull date={appDay(message.time)} />}
					</li>
				))}
			</ol>
		)
	}

	return <TextBlock {...Text.NoActivities} />
}

function StateChecker({ campaign_id: campaignId, state }: Execution) {
	switch (state) {
		case 'InProgress':
		case 'NotStarted':
			return <CheckPaused campaignId={campaignId} />

		default:
			return <CheckScheduled />
	}
}
